import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import PageSkeleton from './PageSkeleton';
import { loginUrlForInbox } from '../utils/loginNextPath';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { firebaseUser, loading: authLoading } = useAuth();
  const { loading: profileLoading } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();

  // Routes that don't require authentication
  const publicRoutes = [
    '/',
    '/login',
    '/startups/pricing',
    '/investors/pricing',
    '/password-reset',
    '/unsubscribe',
    '/express-signup',
  ];

  // Check if current route is public
  const isPublicRoute = publicRoutes.some(route => {
    if (route.includes(':')) {
      // Handle dynamic routes like /finalstep/:userId
      const routePattern = route.split('/:')[0];
      return location.pathname.startsWith(routePattern);
    }
    // Exact match for home route
    if (route === '/') {
      return location.pathname === '/';
    }
    // For other routes, check if path starts with the route
    return location.pathname.startsWith(route);
  });

  useEffect(() => {
    // Don't check if still loading or on public route
    // For dashboard route, don't wait for profile loading (it will handle its own redirect)
    const isDashboardRoute =
      location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/');
    if (authLoading || (profileLoading && !isDashboardRoute) || isPublicRoute) {
      return;
    }

    // Redirect unauthenticated users to landing (e.g. after account deletion / sign out)
    if (!firebaseUser) {
      if (location.pathname === '/settings/notifications') {
        navigate('/unsubscribe', { replace: true });
        return;
      }
      if (location.pathname === '/inbox') {
        navigate(loginUrlForInbox(location.pathname, location.search), { replace: true });
        return;
      }
      navigate('/', { replace: true });
      return;
    }
  }, [
    firebaseUser,
    authLoading,
    profileLoading,
    isPublicRoute,
    navigate,
    location.pathname,
    location.search,
  ]);

  const isDashboardRoute =
    location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/');

  // Show skeleton while auth/profile resolves or while a redirect is in flight.
  // All three cases render the same skeleton so users never see a blank screen or spinner.
  if (authLoading || (profileLoading && !isDashboardRoute) || (!isPublicRoute && !firebaseUser)) {
    return <PageSkeleton />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
